import cloneDeep from 'lodash.clonedeep';
import { PLACEHOLDERS, getPlaceholderByToken } from '@constants/placeholders';

const escapeAttributeValue = value => String(value).replace(/"/g, '&quot;');

const escapeHtmlText = text => String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const escapeRegex = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const placeholderTokenPattern = token => {
    const def = getPlaceholderByToken(token);
    return def?.hasRefId ? `${escapeRegex(token)}:[^\\]\\s]+` : escapeRegex(token);
};

const createMarkTag = ({ placeholder, refId = null, label = null }) => {
    const attrs = [
        'class="pl-custom-highlight"',
        `data-placeholder="${escapeAttributeValue(placeholder)}"`,
        refId ? `data-ref-id="${escapeAttributeValue(refId)}"` : null,
    ]
        .filter(Boolean)
        .join(' ');
    const visibleText = label != null ? label : placeholder;
    return `<mark ${attrs}>${escapeHtmlText(visibleText)}</mark>`;
};

const DEFAULT_TOKENS = ['name', 'last_name', 'location', 'survey', 'coupon'];

// Non-placeholder text is HTML-escaped so user content can't be reinterpreted as markup
// when ProseMirror parses the result.
export const serializePlaceholdersToHtml = (text = '', opts = {}) => {
    if (!text) return '';
    // `??` preserves an explicit `tokens: []` (caller opting out of chips); `||` would not.
    const tokens = opts.tokens ?? DEFAULT_TOKENS;
    const getLabel = opts.getLabel || (token => token);

    const fallbackPattern = opts.allowFallback ? '(?:\\|[^\\]]*)?' : '';
    const parts = tokens
        .filter(token => getPlaceholderByToken(token))
        .map(token =>
            getPlaceholderByToken(token).hasRefId
                ? placeholderTokenPattern(token)
                : `${placeholderTokenPattern(token)}${fallbackPattern}`,
        );
    if (parts.length === 0) {
        return escapeHtmlText(text).replace(/\n/g, '<br>');
    }
    const masterRegex = new RegExp(`\\[(${parts.join('|')})\\]`, 'g');

    let out = '';
    let lastIdx = 0;
    let match;
    while ((match = masterRegex.exec(text)) !== null) {
        out += escapeHtmlText(text.slice(lastIdx, match.index));
        const pipeIdx = opts.allowFallback ? match[1].indexOf('|') : -1;
        const inner = pipeIdx === -1 ? match[1] : match[1].slice(0, pipeIdx);
        const fallback = pipeIdx === -1 ? null : match[1].slice(pipeIdx + 1);
        const colonIdx = inner.indexOf(':');
        const token = colonIdx === -1 ? inner : inner.slice(0, colonIdx);
        const refId = colonIdx === -1 ? null : inner.slice(colonIdx + 1);
        const def = getPlaceholderByToken(token);
        const label =
            fallback && opts.formatFallback ? opts.formatFallback(getLabel(token), fallback) : getLabel(token);
        out += createMarkTag({ placeholder: token, refId, label });
        if (def?.hasRefId) {
            out += '&#8203;';
        }
        lastIdx = match.index + match[0].length;
    }
    out += escapeHtmlText(text.slice(lastIdx));

    return out.replace(/\n/g, '<br>');
};

const needsSeparator = character => character !== '' && !/\s/.test(character);

export const buildPlaceholderInsertion = ({
    token,
    label,
    padded = false,
    characterBefore = '',
    characterAfter = '',
}) => [
    ...(padded && needsSeparator(characterBefore) ? [{ type: 'text', text: ' ' }] : []),
    { type: 'text', text: label, marks: [{ type: 'highlight', attrs: { placeholder: token, refId: null } }] },
    ...(padded && needsSeparator(characterAfter) ? [{ type: 'text', text: ' ' }] : []),
];

// Inverse of parseTipTapJSONToHTML — the editor consumes plain text with [token] markers, not HTML.
export const parseHtmlToEditorText = (html = '') => {
    if (!html) return '';
    return html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/g, '&');
};

export const parseTipTapJSONToPlainText = (json = {}) => {
    let data = cloneDeep(json);
    if (typeof json === 'string') {
        try {
            data = JSON.parse(json);
        } catch (error) {
            return json;
        }
    }

    // Join top-level nodes with '\n' instead of appending '\n' and trimming. Trimming was
    // ambiguous: it removed both the loop's trailing '\n' and the user's leading/trailing
    // whitespace. Live-edit callers (MessagePreview) need keystroke whitespace preserved;
    // call sites that want cleanup should .trim() explicitly.
    const getTextFromMirrorDocument = mirrorValue => {
        const { content } = mirrorValue;
        if (!content || content.length === 0) {
            return '';
        }
        return content.map(getTextFromMirrorNode).join('\n');
    };

    const formatPlaceholder = mark => {
        const placeholder = mark?.attrs?.placeholder;
        if (!placeholder) {
            return null;
        }
        const refId = mark?.attrs?.refId;
        return `[${placeholder}${refId ? `:${refId}` : ''}]`;
    };

    const getTextFromMirrorNode = node => {
        if (!node) {
            return '';
        }

        switch (node.type) {
            case 'text': {
                const highlightMark = node?.marks?.find(mark => mark?.type === 'highlight');
                if (highlightMark) {
                    return formatPlaceholder(highlightMark) || `[${node.text}]`;
                }
                return node.text;
            }
            case 'hardBreak':
                return '\n';
            case 'paragraph':
                if (!node.content) {
                    return '';
                }
                return node.content.map(subNode => getTextFromMirrorNode(subNode)).join('');
            default:
                return '';
        }
    };
    return getTextFromMirrorDocument(data);
};

export const parseTipTapJSONToHTML = (json = {}) => {
    let data = cloneDeep(json);
    if (typeof json === 'string') {
        try {
            data = JSON.parse(json);
        } catch (error) {
            return json;
        }
    }

    const nodeToHTML = node => {
        if (!node) return '';

        switch (node.type) {
            case 'doc':
                return (node.content || []).map(nodeToHTML).join('');
            case 'paragraph': {
                const inner = (node.content || []).map(nodeToHTML).join('');
                return `<p>${inner}</p>`;
            }
            case 'text': {
                const highlightMark = node?.marks?.find(m => m?.type === 'highlight');
                if (highlightMark) {
                    const placeholder = highlightMark.attrs?.placeholder;
                    const refId = highlightMark.attrs?.refId;
                    return `[${placeholder}${refId ? `:${refId}` : ''}]`;
                }
                return escapeHtmlText(node.text || '');
            }
            case 'hardBreak':
                return '<br>';
            default:
                return '';
        }
    };

    return nodeToHTML(data);
};

// Approximates the delivered length: each supported placeholder is discounted down to its
// bare token name (the rough length of what the backend will substitute). `tokens` must
// match the editor's active set — tokens outside it are sent verbatim and counted in full.
export const getEditorContentLength = (value = '', tokens = []) => {
    if (!value) return 0;
    let length = value.length;
    const allowed = new Set(tokens);
    for (const { token, hasRefId } of PLACEHOLDERS) {
        if (hasRefId || !allowed.has(token)) continue;
        const matches = value.match(new RegExp(`\\[${escapeRegex(token)}\\]`, 'g'));
        if (matches) {
            for (const m of matches) {
                length -= m.length - token.length;
            }
        }
    }
    return length;
};

export const testEditorLength = (value, maxLength, tokens = []) => getEditorContentLength(value, tokens) <= maxLength;

// X (Twitter) weighted length — mirrors server XTextLengthCalculator so the composer counter agrees
// with the publish-time check: URLs count as 23, emoji / CJK / fullwidth as 2, the rest as 1.
const X_URL_REGEX = /\bhttps?:\/\/[^\s<>"']+/gi;
const X_URL_TRAILING_TRIM = /[.,;:!?)]+$/;
const X_URL_WEIGHTED_LENGTH = 23;

const xGraphemeSegmenter =
    typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
        ? new Intl.Segmenter('und', { granularity: 'grapheme' })
        : null;

// Mirrors XTextLengthCalculator.IsHeavyBmpRune.
const isHeavyBmpCodePoint = v =>
    (v >= 0x4e00 && v <= 0x9fff) ||
    (v >= 0x3000 && v <= 0xd7af) ||
    (v >= 0xf900 && v <= 0xfaff) ||
    (v >= 0xff00 && v <= 0xffef);

const xGraphemeWeight = grapheme => {
    for (const ch of grapheme) {
        if (ch.codePointAt(0) > 0xffff) return 2;
    }
    return isHeavyBmpCodePoint(grapheme.codePointAt(0)) ? 2 : 1;
};

// Placeholders are reduced to their bare token name first (the proxy getEditorContentLength uses).
export const getXWeightedLength = (value = '', tokens = []) => {
    if (!value) return 0;

    let proxied = value;
    const allowed = new Set(tokens);
    for (const { token, hasRefId } of PLACEHOLDERS) {
        if (hasRefId || !allowed.has(token)) continue;
        proxied = proxied.replace(new RegExp(`\\[${escapeRegex(token)}\\]`, 'g'), token);
    }

    const normalised = proxied.replace(X_URL_REGEX, url => {
        const trimmed = url.replace(X_URL_TRAILING_TRIM, '');
        const trailing = url.slice(trimmed.length);
        return 'x'.repeat(X_URL_WEIGHTED_LENGTH) + trailing;
    });

    let total = 0;
    if (xGraphemeSegmenter) {
        for (const { segment } of xGraphemeSegmenter.segment(normalised)) {
            total += xGraphemeWeight(segment);
        }
    } else {
        for (const ch of normalised) {
            total += xGraphemeWeight(ch);
        }
    }
    return total;
};

export const testXWeightedLength = (value, maxLength, tokens = []) => getXWeightedLength(value, tokens) <= maxLength;
